import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { motion } from "framer-motion";
import { CheckCircle2, Briefcase, ArrowRight, Zap, Mail, LayoutDashboard, Calendar } from "lucide-react";
import {
  BlogNav,
  BlogFooter,
  CTABlock,
  KeyTakeaways,
  FAQSection,
  PostMeta,
  CompetitorDisclaimer,
  trackBlogCTA,
  generateArticleSchema,
  generateFAQSchema
} from "./BlogComponents";

const POST_SLUG = "top-lead-qualification-tools-for-service-businesses";
const POST_DATA = {
  title: "Top Lead Qualification Tools for Service Businesses",
  description: "Discover the best tools to qualify leads automatically. From AI scoring to CRM pipelines, find the right fit for your service business.",
  author: "HeyIntake Team",
  datePublished: "2026-03-01",
  url: `/blog/${POST_SLUG}`
};

const faqs = [
  {
    question: "What is lead qualification?",
    answer: "Lead qualification is the process of evaluating potential customers to determine how likely they are to become paying clients. It involves assessing factors like budget, urgency, fit for your services, and decision-making authority. Effective qualification helps you focus on leads most likely to convert."
  },
  {
    question: "How does AI improve lead qualification?",
    answer: "AI analyzes lead information instantly and consistently, applying the same criteria to every submission. It can process hundreds of leads without fatigue or bias, provide scoring explanations, and identify patterns humans might miss. This speeds up response times and improves prioritization accuracy."
  },
  {
    question: "Do I need a CRM if I use HeyIntake?",
    answer: "HeyIntake includes a built-in pipeline (light CRM) that handles lead tracking through stages, email follow-ups, and contact management. For many service businesses, this is sufficient. If you have an existing CRM you want to keep, you can export leads via CSV and continue your workflow there."
  },
  {
    question: "What types of service businesses benefit most from AI lead scoring?",
    answer: "Any service business that receives multiple inquiries and needs to prioritize follow-up benefits from AI scoring. This includes law firms, consultants, coaches, agencies, home services, contractors, therapists, financial advisors, and similar professionals who qualify clients before engagement."
  },
  {
    question: "How accurate is AI lead scoring?",
    answer: "AI scoring provides a directional signal based on the information submitted. Accuracy depends on asking the right intake questions and having clear qualification criteria. Most users find AI scores align closely with their own manual assessments — but much faster. You always see the reasoning behind each score."
  },
  {
    question: "Can I customize how leads are scored?",
    answer: "Yes. The questions you include in your intake form shape how the AI evaluates submissions. By asking about budget, timeline, specific needs, and other qualification criteria, you guide the AI to score based on what matters for your business."
  }
];

export default function LeadQualificationToolsBlogPost() {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.5 }
  };

  return (
    <>
      <Helmet>
        <title>{POST_DATA.title} | HeyIntake Blog</title>
        <meta name="description" content={POST_DATA.description} />
        <link rel="canonical" href={`https://heyintake.com${POST_DATA.url}`} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateArticleSchema(POST_DATA))
        }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{
          __html: JSON.stringify(generateFAQSchema(faqs))
        }} />
      </Helmet>

      <div className="min-h-screen bg-[#f5f5f0]">
        <BlogNav />

        <article className="py-12 px-4">
          <div className="max-w-3xl mx-auto">
            <motion.div {...fadeIn}>
              {/* Breadcrumb */}
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
                <Link to="/blog" className="hover:text-foreground">Blog</Link>
                <span>/</span>
                <span>Service Businesses</span>
              </div>

              {/* Header */}
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
                {POST_DATA.title}
              </h1>
              
              <PostMeta author={POST_DATA.author} date="March 1, 2026" readTime={10} />

              {/* Key Takeaways */}
              <KeyTakeaways items={[
                "The cost of chasing bad leads compounds: wasted time, missed opportunities, team burnout",
                "Good qualification is fast, consistent, and action-oriented",
                "Tools fall into categories: forms, CRMs, schedulers, automation platforms, and AI scoring",
                "HeyIntake combines AI scoring with a built-in pipeline — no complex integrations needed",
                "The right tool depends on your volume, complexity, and current workflow"
              ]} />

              {/* Intro */}
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-muted-foreground leading-relaxed">
                  Every service business has the same problem: leads come in, but not all of them are worth your time. Some have budget. Some have urgency. Some are just tire-kickers who will never buy.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  The cost of chasing bad leads compounds. You spend hours on calls that go nowhere. Your best prospects wait while you deal with low-quality inquiries. Your team burns out trying to treat every lead like a potential client.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  Lead qualification tools solve this. They help you identify who deserves attention — and who doesn't — before you invest significant time. In this guide, we'll compare the top options for service businesses in 2026.
                </p>
              </div>

              {/* Top CTA */}
              <CTABlock postSlug={POST_SLUG} location="top" variant="compact" />

              {/* Section: What Good Qualification Looks Like */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                What good lead qualification looks like
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  Effective lead qualification has four characteristics:
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-4 my-6">
                {[
                  { title: "Speed", desc: "Qualified leads get responses quickly. Bad leads don't waste your time." },
                  { title: "Consistency", desc: "Every lead is evaluated against the same criteria, regardless of who reviews it." },
                  { title: "Prioritization", desc: "High-value leads rise to the top. You work your best opportunities first." },
                  { title: "Clear next steps", desc: "After qualification, you know exactly what action to take." }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg p-4 border">
                    <h3 className="font-semibold text-foreground mb-1">{item.title}</h3>
                    <p className="text-sm text-muted-foreground">{item.desc}</p>
                  </div>
                ))}
              </div>

              {/* Section: Categories of Tools */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Categories of lead qualification tools
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Not all tools do the same thing. Understanding the categories helps you choose the right combination:
                </p>
              </div>

              <div className="space-y-4">
                {[
                  { 
                    title: "Form builders", 
                    desc: "Collect lead information. Great for data capture, but don't evaluate or prioritize automatically.",
                    examples: "Jotform, Typeform, Google Forms"
                  },
                  { 
                    title: "CRMs", 
                    desc: "Track leads through your sales process. Essential for pipeline management, but require manual qualification unless integrated with other tools.",
                    examples: "Various CRM platforms"
                  },
                  { 
                    title: "Scheduling tools", 
                    desc: "Let qualified leads book time with you. Speed up the conversion process, but don't help you decide who should be booking.",
                    examples: "Calendly, Acuity, Cal.com"
                  },
                  { 
                    title: "Automation platforms", 
                    desc: "Connect tools together and automate workflows. Powerful, but complex to set up and maintain.",
                    examples: "Zapier, Make, various automation tools"
                  },
                  { 
                    title: "AI scoring tools", 
                    desc: "Automatically evaluate and prioritize leads based on their responses. The newest category — and often the most impactful.",
                    examples: "HeyIntake"
                  }
                ].map((item, i) => (
                  <div key={i} className="bg-white rounded-lg p-5 border">
                    <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                    <p className="text-muted-foreground text-sm mb-2">{item.desc}</p>
                    <p className="text-xs text-muted-foreground italic">Examples: {item.examples}</p>
                  </div>
                ))}
              </div>

              {/* Section: Top Tools */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Top lead qualification tools by category
              </h2>

              {/* HeyIntake */}
              <div className="bg-primary/5 border-2 border-primary rounded-xl p-6 my-6">
                <div className="flex items-center gap-2 mb-3">
                  <Zap className="w-6 h-6 text-primary" />
                  <h3 className="text-xl font-bold text-foreground">Best AI Scoring + Prioritization: HeyIntake</h3>
                </div>
                <p className="text-muted-foreground mb-4">
                  HeyIntake is built for service businesses that need to qualify leads fast. It combines AI scoring with a built-in pipeline, so you don't need separate tools for form building, lead evaluation, and tracking.
                </p>
                <ul className="space-y-2 mb-4">
                  {[
                    "AI scores every submission instantly (lead scoring + intake scoring)",
                    "Plain-English explanations for every score",
                    "Visual pipeline to track leads through stages",
                    "Email clients directly from the app",
                    "Include scheduling links in follow-ups",
                    "CSV import/export for contacts"
                  ].map((item, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                      <span className="text-foreground">{item}</span>
                    </li>
                  ))}
                </ul>
                <p className="text-sm text-muted-foreground">
                  Best for: Agencies, consultants, coaches, law firms, home services, and any service business receiving multiple inquiries.
                </p>
              </div>

              {/* Other options */}
              <div className="space-y-4">
                <div className="bg-white rounded-xl p-6 border">
                  <h3 className="text-lg font-semibold text-foreground mb-2">Best General-Purpose Form Builder: Jotform</h3>
                  <p className="text-muted-foreground text-sm">
                    Jotform is widely used as a flexible form builder with thousands of templates. It's great for data collection across many industries, though AI-powered lead scoring would require third-party integrations.
                  </p>
                  <CompetitorDisclaimer />
                </div>

                <div className="bg-white rounded-xl p-6 border">
                  <h3 className="text-lg font-semibold text-foreground mb-2">Best Industry-Specific Workflows: IntakeQ</h3>
                  <p className="text-muted-foreground text-sm">
                    IntakeQ is commonly positioned toward healthcare and clinic-style intake workflows. It includes scheduling features and is popular with practices that need appointment-driven intake processes.
                  </p>
                  <CompetitorDisclaimer />
                </div>
              </div>

              <p className="text-sm text-muted-foreground mt-4">
                <Link to="/compare/heyintake-vs-intakeq-vs-jotform" className="text-primary hover:underline">
                  See our detailed comparison: HeyIntake vs IntakeQ vs Jotform →
                </Link>
              </p>

              {/* Mid CTA */}
              <CTABlock postSlug={POST_SLUG} location="mid" />

              {/* Section: How to Choose */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                How to choose based on your workflow
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed">
                  The right tool depends on your situation:
                </p>
              </div>

              <div className="bg-white rounded-xl p-6 border my-6">
                <ul className="space-y-4">
                  <li>
                    <strong className="text-foreground">High volume, need prioritization?</strong>
                    <p className="text-sm text-muted-foreground">Choose an AI scoring tool like HeyIntake. Manual review doesn't scale.</p>
                  </li>
                  <li>
                    <strong className="text-foreground">Just need to collect information?</strong>
                    <p className="text-sm text-muted-foreground">A form builder might be enough — but you'll review every submission manually.</p>
                  </li>
                  <li>
                    <strong className="text-foreground">Already have a CRM you love?</strong>
                    <p className="text-sm text-muted-foreground">Look for tools that export data easily. HeyIntake offers CSV export for this reason.</p>
                  </li>
                  <li>
                    <strong className="text-foreground">Need scheduling built-in?</strong>
                    <p className="text-sm text-muted-foreground">Some tools include native scheduling. Others (like HeyIntake) integrate via scheduling links in follow-up emails.</p>
                  </li>
                </ul>
              </div>

              {/* Section: Quick-Start Workflow */}
              <h2 className="text-2xl font-bold text-foreground mt-12 mb-4">
                Quick-start workflow with HeyIntake
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Here's how a typical service business workflow looks with HeyIntake:
                </p>
              </div>

              <div className="space-y-4 my-6">
                {[
                  { step: 1, icon: <Briefcase className="w-5 h-5" />, title: "Capture the lead", desc: "Prospect fills out your intake form. AI scores the submission instantly." },
                  { step: 2, icon: <Zap className="w-5 h-5" />, title: "Review the score", desc: "You see a score (1-10) plus an explanation of why they scored that way." },
                  { step: 3, icon: <Mail className="w-5 h-5" />, title: "Send follow-up", desc: "Email qualified leads directly from HeyIntake. Use templates for common responses." },
                  { step: 4, icon: <Calendar className="w-5 h-5" />, title: "Include scheduling link", desc: "Add your Calendly/Acuity link so qualified leads can book immediately." },
                  { step: 5, icon: <LayoutDashboard className="w-5 h-5" />, title: "Track in pipeline", desc: "Move leads through stages as they progress. See your entire funnel at a glance." }
                ].map((item) => (
                  <div key={item.step} className="flex items-start gap-4 bg-white rounded-lg p-4 border">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary flex-shrink-0">
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-foreground">Step {item.step}: {item.title}</h3>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* FAQ Section */}
              <FAQSection faqs={faqs} postSlug={POST_SLUG} />

              {/* Footer CTA */}
              <CTABlock postSlug={POST_SLUG} location="footer" />

              {/* Related Links */}
              <div className="mt-12 pt-8 border-t">
                <h3 className="text-lg font-semibold text-foreground mb-4">Related Resources</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/intake-form-for-consultants" className="text-primary hover:underline">
                      HeyIntake for Consultants →
                    </Link>
                  </li>
                  <li>
                    <Link to="/intake-form-for-agencies" className="text-primary hover:underline">
                      HeyIntake for Agencies →
                    </Link>
                  </li>
                  <li>
                    <Link to="/intake-form-for-coaches" className="text-primary hover:underline">
                      HeyIntake for Coaches →
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog/how-to-automate-client-screening-with-ai" className="text-primary hover:underline">
                      How to Automate Client Screening with AI →
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-primary hover:underline">
                      View Pricing →
                    </Link>
                  </li>
                </ul>
              </div>
            </motion.div>
          </div>
        </article>

        <BlogFooter />
      </div>
    </>
  );
}
